import { useMutation, useQueryClient } from '@tanstack/react-query';

import { createWorkspaceSecret } from '@/lib/api/generated/api';
import type {
  CreateWorkspaceSecretRequest,
  WorkspaceSecretView,
} from '@/lib/api/generated/model';

import { toSecretError } from '../errors';
import { secretKeys } from '../queryKeys';

/**
 * Store a new secret or variable on a workspace.
 *
 * The response is a discriminated union over `status`, so the success branch is
 * narrowed rather than asserted, and everything else becomes a typed refusal the
 * dialog can attach to the right field (`errors.ts`, research D3).
 *
 * On success the list is invalidated rather than spliced. Optimism would buy
 * little for a deliberate administrative action, and the optimistic row could
 * not even be rendered correctly: the service assigns the timestamps, and a
 * secret's `value` comes back `null` no matter what was typed (research D7).
 */
export function useCreateWorkspaceSecret(workspaceId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (
      body: CreateWorkspaceSecretRequest,
    ): Promise<WorkspaceSecretView> => {
      const response = await createWorkspaceSecret(workspaceId, body);
      if (response.status === 201) return response.data;
      throw toSecretError(response.status, response.data);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: secretKeys.all(workspaceId),
      });
    },
  });
}
